import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type Locale = 'en' | 'es' | 'fr' | 'de' | 'zh' | 'hi';

interface TranslationMap {
  [key: string]: string;
}

interface TranslationsMap {
  [locale: string]: TranslationMap;
}

interface LanguageContextProps {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string) => string;
  translations: TranslationsMap;
}

const translations: TranslationsMap = {
  en: {
    // General
    'app.name': 'Chatrix',
    'app.tagline': 'Connect with people around the world',
    
    // Auth
    'auth.login': 'Log In',
    'auth.signup': 'Sign Up',
    'auth.logout': 'Log Out',
    'auth.email': 'Email',
    'auth.password': 'Password',
    'auth.forgotPassword': 'Forgot Password?',
    
    // Navigation
    'nav.home': 'Home',
    'nav.chat': 'Chat',
    'nav.about': 'About',
    'nav.profile': 'Profile',
    'nav.settings': 'Settings',
    'nav.points': 'Aura Points',
    
    // Profile
    'profile.title': 'Your Profile',
    'profile.editPhoto': 'Change Photo',
    'profile.memberSince': 'Member since',
    'profile.notifications': 'Notifications',
    'profile.points': 'Aura Points',
    'profile.signIn': 'Sign in to view your profile',
    'profile.needLogin': 'You need to be logged in to access your profile information',
    'profile.nameUpdated': 'Name updated successfully',
    'profile.photoUpdated': 'Profile photo updated successfully',
    'profile.updateFailed': 'Update failed. Please try again.',
    'profile.imageTooLarge': 'Image too large (max 2MB)',
    'profile.invalidImageType': 'Please select a valid image file',
    'profile.enterName': 'Enter your name',
    
    // Settings
    'settings.title': 'Your Settings',
    'settings.theme': 'Theme Mode',
    'settings.lightMode': 'Light Mode',
    'settings.darkMode': 'Dark Mode',
    'settings.language': 'Language',
    'settings.notifications': 'Notifications',
    'settings.accessibility': 'Accessibility',
    'settings.privacy': 'Privacy & Security',
    'settings.customize': 'Customize',
    'settings.configure': 'Configure',
    'settings.needLogin': 'You need to be logged in to access settings',
    
    // Chat
    'chat.findNext': 'Next Partner',
    'chat.endChat': 'End Chat',
    'chat.chatEnded': 'Chat Ended',
    'chat.returningHome': 'Returning to homepage...',
    'chat.rateExperience': 'Rate your experience',
    'chat.cancel': 'Cancel',
    'chat.exit': 'Exit',
    
    // Feedback
    'feedback.title': 'Send Feedback',
    'feedback.message': 'Your feedback helps us improve',
    'feedback.send': 'Send',
    'feedback.giveUsFeedback': 'Give us feedback',
    
    // Common
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.saving': 'Saving...',
    
    // Notifications
    'notification.success': 'Success',
    'notification.error': 'Error',
    'notification.info': 'Information',
    
    // Features
    'feature.comingSoon': 'This feature is coming soon!'
  },
  es: {
    // General
    'app.name': 'Chatrix',
    'app.tagline': 'Conéctate con personas de todo el mundo',
    
    // Auth
    'auth.login': 'Iniciar Sesión',
    'auth.signup': 'Registrarse',
    'auth.logout': 'Cerrar Sesión',
    'auth.email': 'Correo Electrónico',
    'auth.password': 'Contraseña',
    'auth.forgotPassword': '¿Olvidaste tu contraseña?',
    
    // Navigation
    'nav.home': 'Inicio',
    'nav.chat': 'Chat',
    'nav.about': 'Acerca de',
    'nav.profile': 'Perfil',
    'nav.settings': 'Configuración',
    'nav.points': 'Puntos Aura',
    
    // Profile
    'profile.title': 'Tu Perfil',
    'profile.editPhoto': 'Cambiar Foto',
    'profile.memberSince': 'Miembro desde',
    'profile.notifications': 'Notificaciones',
    'profile.points': 'Puntos Aura',
    'profile.signIn': 'Inicia sesión para ver tu perfil',
    'profile.needLogin': 'Necesitas iniciar sesión para acceder a la información de tu perfil',
    'profile.nameUpdated': 'Nombre actualizado con éxito',
    'profile.photoUpdated': 'Foto de perfil actualizada con éxito',
    'profile.updateFailed': 'Error al actualizar. Por favor, inténtelo de nuevo.',
    'profile.imageTooLarge': 'La imagen es demasiado grande (máx. 2MB)',
    'profile.invalidImageType': 'Por favor, seleccione un archivo de imagen válido',
    'profile.enterName': 'Introduce tu nombre',
    
    // Settings
    'settings.title': 'Tu Configuración',
    'settings.theme': 'Modo de Tema',
    'settings.lightMode': 'Modo Claro',
    'settings.darkMode': 'Modo Oscuro',
    'settings.language': 'Idioma',
    'settings.notifications': 'Notificaciones',
    'settings.accessibility': 'Accesibilidad',
    'settings.privacy': 'Privacidad y Seguridad',
    'settings.customize': 'Personalizar',
    'settings.configure': 'Configurar',
    'settings.needLogin': 'Necesitas iniciar sesión para acceder a la configuración',
    
    // Chat
    'chat.findNext': 'Siguiente Compañero',
    'chat.endChat': 'Finalizar Chat',
    'chat.chatEnded': 'Chat Finalizado',
    'chat.returningHome': 'Volviendo a la página principal...',
    'chat.rateExperience': 'Califica tu experiencia',
    'chat.cancel': 'Cancelar',
    'chat.exit': 'Salir',
    
    // Feedback
    'feedback.title': 'Enviar Comentarios',
    'feedback.message': 'Tus comentarios nos ayudan a mejorar',
    'feedback.send': 'Enviar',
    'feedback.giveUsFeedback': 'Danos tu opinión',
    
    // Common
    'common.save': 'Guardar',
    'common.cancel': 'Cancelar',
    'common.saving': 'Guardando...',
    
    // Notifications
    'notification.success': 'Éxito',
    'notification.error': 'Error',
    'notification.info': 'Información',
    
    // Features
    'feature.comingSoon': '¡Esta función estará disponible pronto!'
  },
  fr: {
    // General
    'app.name': 'Chatrix',
    'app.tagline': 'Connectez-vous avec des personnes du monde entier',
    
    // Auth
    'auth.login': 'Se Connecter',
    'auth.signup': 'S\'inscrire',
    'auth.logout': 'Se Déconnecter',
    'auth.email': 'Email',
    'auth.password': 'Mot de passe',
    'auth.forgotPassword': 'Mot de passe oublié?',
    
    // Navigation
    'nav.home': 'Accueil',
    'nav.chat': 'Chat',
    'nav.about': 'À propos',
    'nav.profile': 'Profil',
    'nav.settings': 'Paramètres',
    'nav.points': 'Points Aura',
    
    // Profile
    'profile.title': 'Votre Profil',
    'profile.editPhoto': 'Changer la Photo',
    'profile.memberSince': 'Membre depuis',
    'profile.notifications': 'Notifications',
    'profile.points': 'Points Aura',
    'profile.signIn': 'Connectez-vous pour voir votre profil',
    'profile.needLogin': 'Vous devez être connecté pour accéder aux informations de votre profil',
    'profile.nameUpdated': 'Nom mis à jour avec succès',
    'profile.photoUpdated': 'Photo de profil mise à jour avec succès',
    'profile.updateFailed': 'La mise à jour a échoué. Veuillez réessayer.',
    'profile.imageTooLarge': 'Image trop grande (max. 2 Mo)',
    'profile.invalidImageType': 'Veuillez sélectionner un fichier image valide',
    'profile.enterName': 'Entrez votre nom',
    
    // Settings
    'settings.title': 'Vos Paramètres',
    'settings.theme': 'Mode Thème',
    'settings.lightMode': 'Mode Clair',
    'settings.darkMode': 'Mode Sombre',
    'settings.language': 'Langue',
    'settings.notifications': 'Notifications',
    'settings.accessibility': 'Accessibilité',
    'settings.privacy': 'Confidentialité et Sécurité',
    'settings.customize': 'Personnaliser',
    'settings.configure': 'Configurer',
    'settings.needLogin': 'Vous devez être connecté pour accéder aux paramètres',
    
    // Chat
    'chat.findNext': 'Partenaire Suivant',
    'chat.endChat': 'Terminer le Chat',
    'chat.chatEnded': 'Chat Terminé',
    'chat.returningHome': 'Retour à la page d\'accueil...',
    'chat.rateExperience': 'Évaluez votre expérience',
    'chat.cancel': 'Annuler',
    'chat.exit': 'Quitter',
    
    // Feedback
    'feedback.title': 'Envoyer un Retour',
    'feedback.message': 'Vos commentaires nous aident à améliorer',
    'feedback.send': 'Envoyer',
    'feedback.giveUsFeedback': 'Donnez-nous votre avis',
    
    // Common
    'common.save': 'Enregistrer',
    'common.cancel': 'Annuler',
    'common.saving': 'Enregistrement...',
    
    // Notifications
    'notification.success': 'Succès',
    'notification.error': 'Erreur',
    'notification.info': 'Information',
    
    // Features
    'feature.comingSoon': 'Cette fonctionnalité sera bientôt disponible!'
  },
  de: {
    // General
    'app.name': 'Chatrix',
    'app.tagline': 'Verbinde dich mit Menschen auf der ganzen Welt',
    
    // Auth
    'auth.login': 'Anmelden',
    'auth.signup': 'Registrieren',
    'auth.logout': 'Abmelden',
    'auth.email': 'E-Mail',
    'auth.password': 'Passwort',
    'auth.forgotPassword': 'Passwort vergessen?',
    
    // Navigation
    'nav.home': 'Startseite',
    'nav.chat': 'Chat',
    'nav.about': 'Über uns',
    'nav.profile': 'Profil',
    'nav.settings': 'Einstellungen',
    'nav.points': 'Aura-Punkte',
    
    // Profile
    'profile.title': 'Dein Profil',
    'profile.editPhoto': 'Foto ändern',
    'profile.memberSince': 'Mitglied seit',
    'profile.notifications': 'Benachrichtigungen',
    'profile.points': 'Aura-Punkte',
    'profile.signIn': 'Melde dich an, um dein Profil zu sehen',
    'profile.needLogin': 'Du musst angemeldet sein, um auf deine Profilinformationen zuzugreifen',
    'profile.nameUpdated': 'Name erfolgreich aktualisiert',
    'profile.photoUpdated': 'Profilfoto erfolgreich aktualisiert',
    'profile.updateFailed': 'Update fehlgeschlagen. Bitte versuche es erneut.',
    'profile.imageTooLarge': 'Bild zu groß (max. 2MB)',
    'profile.invalidImageType': 'Bitte wähle eine gültige Bilddatei aus',
    'profile.enterName': 'Gib deinen Namen ein',
    
    // Settings
    'settings.title': 'Deine Einstellungen',
    'settings.theme': 'Themen-Modus',
    'settings.lightMode': 'Heller Modus',
    'settings.darkMode': 'Dunkler Modus',
    'settings.language': 'Sprache',
    'settings.notifications': 'Benachrichtigungen',
    'settings.accessibility': 'Barrierefreiheit',
    'settings.privacy': 'Datenschutz & Sicherheit',
    'settings.customize': 'Anpassen',
    'settings.configure': 'Konfigurieren',
    'settings.needLogin': 'Du musst angemeldet sein, um auf die Einstellungen zuzugreifen',
    
    // Chat
    'chat.findNext': 'Nächster Partner',
    'chat.endChat': 'Chat beenden',
    'chat.chatEnded': 'Chat beendet',
    'chat.returningHome': 'Zurück zur Startseite...',
    'chat.rateExperience': 'Bewerte deine Erfahrung',
    'chat.cancel': 'Abbrechen',
    'chat.exit': 'Beenden',
    
    // Feedback
    'feedback.title': 'Feedback senden',
    'feedback.message': 'Dein Feedback hilft uns, besser zu werden',
    'feedback.send': 'Senden',
    'feedback.giveUsFeedback': 'Gib uns dein Feedback',
    
    // Common
    'common.save': 'Speichern',
    'common.cancel': 'Abbrechen',
    'common.saving': 'Speichern...',
    
    // Notifications
    'notification.success': 'Erfolg',
    'notification.error': 'Fehler',
    'notification.info': 'Information',
    
    // Features
    'feature.comingSoon': 'Diese Funktion wird bald verfügbar sein!'
  },
  zh: {
    // General
    'app.name': 'Chatrix',
    'app.tagline': '与世界各地的人联系',
    
    // Auth
    'auth.login': '登录',
    'auth.signup': '注册',
    'auth.logout': '退出登录',
    'auth.email': '电子邮件',
    'auth.password': '密码',
    'auth.forgotPassword': '忘记密码？',
    
    // Navigation
    'nav.home': '首页',
    'nav.chat': '聊天',
    'nav.about': '关于',
    'nav.profile': '个人资料',
    'nav.settings': '设置',
    'nav.points': '光环点数',
    
    // Profile
    'profile.title': '您的个人资料',
    'profile.editPhoto': '更改照片',
    'profile.memberSince': '成为会员自',
    'profile.notifications': '通知',
    'profile.points': '光环点数',
    'profile.signIn': '登录以查看您的个人资料',
    'profile.needLogin': '您需要登录才能访问您的个人资料信息',
    'profile.nameUpdated': '姓名已成功更新',
    'profile.photoUpdated': '个人资料照片已成功更新',
    'profile.updateFailed': '更新失败。请重试。',
    'profile.imageTooLarge': '图片太大 (最大 2MB)',
    'profile.invalidImageType': '请选择有效的图片文件',
    'profile.enterName': '输入你的名字',
    
    // Settings
    'settings.title': '您的设置',
    'settings.theme': '主题模式',
    'settings.lightMode': '浅色模式',
    'settings.darkMode': '深色模式',
    'settings.language': '语言',
    'settings.notifications': '通知',
    'settings.accessibility': '无障碍',
    'settings.privacy': '隐私与安全',
    'settings.customize': '自定义',
    'settings.configure': '配置',
    'settings.needLogin': '您需要登录才能访问设置',
    
    // Chat
    'chat.findNext': '下一个伙伴',
    'chat.endChat': '结束聊天',
    'chat.chatEnded': '聊天已结束',
    'chat.returningHome': '返回首页...',
    'chat.rateExperience': '评价您的体验',
    'chat.cancel': '取消',
    'chat.exit': '退出',
    
    // Feedback
    'feedback.title': '发送反馈',
    'feedback.message': '您的反馈帮助我们改进',
    'feedback.send': '发送',
    'feedback.giveUsFeedback': '给我们您的反馈',
    
    // Common
    'common.save': '保存',
    'common.cancel': '取消',
    'common.saving': '保存中...',
    
    // Notifications
    'notification.success': '成功',
    'notification.error': '错误',
    'notification.info': '信息',
    
    // Features
    'feature.comingSoon': '此功能即将推出！'
  },
  hi: {
    // General
    'app.name': 'चैट्रिक्स',
    'app.tagline': 'दुनिया भर के लोगों से जुड़ें',
    
    // Auth
    'auth.login': 'लॉग इन',
    'auth.signup': 'साइन अप',
    'auth.logout': 'लॉग आउट',
    'auth.email': 'ईमेल',
    'auth.password': 'पासवर्ड',
    'auth.forgotPassword': 'पासवर्ड भूल गए?',
    
    // Navigation
    'nav.home': 'होम',
    'nav.chat': 'चैट',
    'nav.about': 'हमारे बारे में',
    'nav.profile': 'प्रोफाइल',
    'nav.settings': 'सेटिंग्स',
    'nav.points': 'ऑरा पॉइंट्स',
    
    // Profile
    'profile.title': 'आपकी प्रोफाइल',
    'profile.editPhoto': 'फोटो बदलें',
    'profile.memberSince': 'कब से सदस्य',
    'profile.notifications': 'सूचनाएँ',
    'profile.points': 'ऑरा पॉइंट्स',
    'profile.signIn': 'अपनी प्रोफाइल देखने के लिए साइन इन करें',
    'profile.needLogin': 'आपको अपनी प्रोफाइल जानकारी तक पहुंचने के लिए लॉग इन करना होगा',
    'profile.nameUpdated': 'नाम सफलतापूर्वक अपडेट किया गया',
    'profile.photoUpdated': 'प्रोफाइल फोटो सफलतापूर्वक अपडेट किया गया',
    'profile.updateFailed': 'अपडेट विफल। कृपया पुनः प्रयास करें।',
    'profile.imageTooLarge': 'छवि बहुत बड़ी है (अधिकतम 2MB)',
    'profile.invalidImageType': 'कृपया एक वैध छवि फाइल चुनें',
    'profile.enterName': 'अपना नाम दर्ज करें',
    
    // Settings
    'settings.title': 'आपकी सेटिंग्स',
    'settings.theme': 'थीम मोड',
    'settings.lightMode': 'लाइट मोड',
    'settings.darkMode': 'डार्क मोड',
    'settings.language': 'भाषा',
    'settings.notifications': 'सूचनाएँ',
    'settings.accessibility': 'एक्सेसिबिलिटी',
    'settings.privacy': 'प्राइवेसी और सुरक्षा',
    'settings.customize': 'कस्टमाइज़',
    'settings.configure': 'कॉन्फिगर',
    'settings.needLogin': 'सेटिंग्स तक पहुंचने के लिए आपको लॉग इन करना होगा',
    
    // Chat
    'chat.findNext': 'अगला पार्टनर',
    'chat.endChat': 'चैट समाप्त करें',
    'chat.chatEnded': 'चैट समाप्त हुई',
    'chat.returningHome': 'होमपेज पर वापस जा रहे हैं...',
    'chat.rateExperience': 'अपने अनुभव का मूल्यांकन करें',
    'chat.cancel': 'रद्द करें',
    'chat.exit': 'बाहर निकलें',
    
    // Feedback
    'feedback.title': 'फीडबैक भेजें',
    'feedback.message': 'आपका फीडबैक हमें सुधार करने में मदद करता है',
    'feedback.send': 'भेजें',
    'feedback.giveUsFeedback': 'हमें अपनी प्रतिक्रिया दें',
    
    // Common
    'common.save': 'सहेजें',
    'common.cancel': 'रद्द करें',
    'common.saving': 'सहेज रहे हैं...',
    
    // Notifications
    'notification.success': 'सफलता',
    'notification.error': 'त्रुटि',
    'notification.info': 'जानकारी',
    
    // Features
    'feature.comingSoon': 'यह फीचर जल्द ही आ रहा है!'
  }
};

const defaultLocale: Locale = 'en';

const LanguageContext = createContext<LanguageContextProps>({
  locale: defaultLocale,
  setLocale: () => {},
  t: (key: string) => key,
  translations
});

export const useLanguage = () => useContext(LanguageContext);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [locale, setLocale] = useState<Locale>(() => {
    // Try to get locale from localStorage
    const savedLocale = localStorage.getItem('locale');
    if (savedLocale && ['en', 'es', 'fr', 'de', 'zh', 'hi'].includes(savedLocale)) {
      return savedLocale as Locale;
    }
    
    // Try to get browser locale
    const browserLocale = navigator.language.split('-')[0];
    if (['en', 'es', 'fr', 'de', 'zh', 'hi'].includes(browserLocale)) {
      return browserLocale as Locale;
    }
    
    return defaultLocale;
  });
  
  useEffect(() => {
    localStorage.setItem('locale', locale);
    document.documentElement.lang = locale;
  }, [locale]);
  
  const t = (key: string): string => {
    const translationMap = translations[locale] || translations[defaultLocale];
    return translationMap[key] || translations[defaultLocale][key] || key;
  };
  
  const value = {
    locale,
    setLocale,
    t,
    translations
  };
  
  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
